import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Shield, Database, Eye, Lock, Smartphone } from 'lucide-react';

const EmergencyCardPrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-[#D92B2B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              const basePath = window.location.pathname.includes('emergency-app') ? '/emergency-app' : '/';
              navigate(basePath);
            }}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Privacy Policy</h1>
            <p className="text-xs text-[#FECACA]">Emergency Card</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 pb-12">
        {/* Key Privacy Feature */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="font-bold text-green-800">Own Your Data</h2>
                <p className="text-sm text-green-700 mt-1">
                  Your medical information is encoded directly in your QR code. 
                  We never store, see, or have access to your personal health data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Last updated: March 2, 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Database className="h-5 w-5 text-[#D92B2B]" />
              What We DON'T Collect
            </h2>
            <div className="bg-slate-50 p-4 rounded-lg">
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✗</span>
                  <span>Your name, date of birth, or personal identifiers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✗</span>
                  <span>Your medical conditions, allergies, or medications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✗</span>
                  <span>Your emergency contact information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✗</span>
                  <span>Your QR code or its contents</span>
                </li>
              </ul>
            </div>
            <p className="text-slate-600 mt-3">
              All your medical information is encoded directly into the QR code image that you save to your device. 
              This data never touches our servers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Eye className="h-5 w-5 text-[#D92B2B]" />
              What We DO Collect
            </h2>
            <div className="bg-slate-50 p-4 rounded-lg">
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">✓</span>
                  <span><strong>Payment Information:</strong> Processed securely by Stripe. We receive confirmation of payment but not your card details.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">✓</span>
                  <span><strong>Transaction Records:</strong> Payment reference ID, amount, date, and status for customer support purposes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">✓</span>
                  <span><strong>Basic Analytics:</strong> Anonymous usage data (page views, button clicks) to improve the service.</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#D92B2B]" />
              How Your Data Works
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#FEE2E2] rounded-full flex items-center justify-center text-[#D92B2B] font-bold">1</div>
                <div>
                  <p className="font-medium text-slate-800">You Enter Information</p>
                  <p className="text-sm text-slate-600">Your medical details are entered in the form on your device.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#FEE2E2] rounded-full flex items-center justify-center text-[#D92B2B] font-bold">2</div>
                <div>
                  <p className="font-medium text-slate-800">QR Code Generated Locally</p>
                  <p className="text-sm text-slate-600">The QR code is created in your browser. Data is encoded directly into the image.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#FEE2E2] rounded-full flex items-center justify-center text-[#D92B2B] font-bold">3</div>
                <div>
                  <p className="font-medium text-slate-800">You Save the QR Code</p>
                  <p className="text-sm text-slate-600">The image is saved to YOUR device. We don't keep a copy.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#FEE2E2] rounded-full flex items-center justify-center text-[#D92B2B] font-bold">4</div>
                <div>
                  <p className="font-medium text-slate-800">Anyone Can Scan</p>
                  <p className="text-sm text-slate-600">When scanned, the QR code opens a viewer page that decodes and displays your info.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#D92B2B]" />
              Security
            </h2>
            <ul className="space-y-2 text-slate-700">
              <li><strong>No Database Breach Risk:</strong> We can't leak data we don't have.</li>
              <li><strong>HTTPS Encryption:</strong> All connections are encrypted in transit.</li>
              <li><strong>Stripe Security:</strong> Payment processing meets PCI DSS standards.</li>
              <li><strong>You Control Your Data:</strong> Delete your QR code image to delete your data.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Third-Party Services</h2>
            <ul className="space-y-2 text-slate-700">
              <li><strong>Stripe:</strong> Payment processing. See <a href="https://stripe.com/privacy" className="text-[#D92B2B] hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Your Rights</h2>
            <ul className="space-y-2 text-slate-700">
              <li><strong>Access:</strong> Your data is in your QR code - you have full access.</li>
              <li><strong>Deletion:</strong> Delete the QR image from your device to delete your data.</li>
              <li><strong>Portability:</strong> Your QR code works anywhere - it's just an image.</li>
              <li><strong>No Account Required:</strong> We don't create accounts or profiles for you.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Children's Privacy</h2>
            <p className="text-slate-700">
              Emergency Card can be used by parents/guardians to create cards for children. 
              Since we don't store any personal data, there is no collection of children's information on our servers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Changes to This Policy</h2>
            <p className="text-slate-700">
              We may update this policy from time to time. Changes will be posted on this page with an updated revision date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Contact Us</h2>
            <p className="text-slate-700">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-[#D92B2B] font-medium mt-2">
              support@myemergencycard.app
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default EmergencyCardPrivacyPolicy;
